/**
 * Controlled command dialog with keyboard selection, planner navigation, activity creation,
 * natural-language scheduling, and direct water/state logging. A processing guard prevents
 * duplicate submissions; command results update stores optimistically before snapshot-aware
 * reconciliation.
 *
 * @module apps/web/src/components/command/CommandPalette
 */

import React, { useState, useEffect, useEffectEvent, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { captureCalendarReadFence } from '@/lib/browserGhostBlocks';
import { useScheduleStore } from '@/stores/scheduleStore';
import { Command, CornerDownLeft, Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useTasksStore, captureTaskSnapshot, isTaskSnapshotCurrent } from '@/stores/tasksStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import {
  taskToUI,
  type NlpCommandResult,
  type ScheduleIntent,
  type TaskUI,
  type WaterUnit,
} from '@fluxability/types';
import type { GhostBlocksData } from '@fluxability/types';
import { openNewActivityFlow } from '@/components/activities/NewActivityFlow';

/** Controlled visibility and closure boundary for the global command dialog. */
interface CommandPaletteProps {
  /** Whether the controlled dialog is visible. */
  open: boolean;
  /** Ask the owner to close the dialog. */
  onClose: () => void;
}

/** Selectable palette entry representing either a local action or a natural-language command suggestion. */
interface CommandSuggestion {
  /** Suggestion category used to distinguish activities from commands. */
  type: 'action' | 'nlp';
  /** Primary label shown and selected in the command palette. */
  text: string;
  /** Optional supporting text explaining the suggestion. */
  description?: string;
  /** Callback executed when the user chooses the suggestion. */
  action?: () => void;
}

/**
 * Controlled command dialog with keyboard selection, planner navigation, activity creation,
 * natural-language scheduling, and direct water/state logging. A processing guard prevents
 * duplicate submissions; command results update stores optimistically before snapshot-aware
 * reconciliation.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [input, setInput] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const processingRef = useRef(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const setBlocks = useScheduleStore(s => s.setBlocks);
  const setTasks = useTasksStore(s => s.setTasks);

  const openNewFlow = (mode: 'choose' | 'personal' | 'team') => {
    onClose();
    window.setTimeout(() => openNewActivityFlow(mode), 0);
  };

  // Default commands
  const defaultCommands: CommandSuggestion[] = [
    {
      type: 'action',
      text: 'New activity',
      description: 'Choose personal or team activity',
      action: () => openNewFlow('choose'),
    },
    {
      type: 'action',
      text: 'Add activity',
      description: 'Create a personal activity',
      action: () => openNewFlow('personal'),
    },
    {
      type: 'action',
      text: 'Add team activity',
      description: 'Assign a shared team activity',
      action: () => openNewFlow('team'),
    },
    {
      type: 'action',
      text: 'Auto-schedule',
      description: 'Optimize unscheduled activities and movable routine blocks',
      action: () => {
        // A unique navigation intent is consumed once by the browser-first calendar controller.
        navigate('/week', { state: { scheduleIntent: crypto.randomUUID() } });
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Week view',
      description: 'Switch to week view',
      action: () => {
        navigate('/week');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Day view',
      description: 'Switch to day view',
      action: () => {
        navigate('/day');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Month view',
      description: 'Switch to month view',
      action: () => {
        navigate('/month');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Kanban view',
      description: 'Switch to kanban view',
      action: () => {
        navigate('/kanban');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Table view',
      description: 'Switch to table view',
      action: () => {
        navigate('/table');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Open Flux Agent',
      description: 'Talk through your energy and let the coach adjust your plan',
      action: () => {
        navigate('/agent');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'State',
      description: 'Log water, sleep, and current energy',
      action: () => {
        navigate('/state');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Analytics',
      description: 'View productivity stats',
      action: () => {
        navigate('/analytics');
        onClose();
      },
    },
  ];

  const [suggestions, setSuggestions] = useState<CommandSuggestion[]>(defaultCommands);

  // defaultCommands is rebuilt every render, so the effects below read it through effect events
  // and run only when the dialog closes or the input changes.
  const resetPalette = useEffectEvent(() => {
    setInput('');
    setSuggestions(defaultCommands);
    setSelectedIndex(0);
    setIsProcessing(false);
    setStatusMessage('');
  });

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) resetPalette();
  }, [open]);

  const filterSuggestions = useEffectEvent((input: string) => {
    setSelectedIndex(0);

    if (!input.trim()) {
      setSuggestions(defaultCommands);
      return;
    }

    const filtered = defaultCommands.filter(cmd =>
      cmd.text.toLowerCase().includes(input.toLowerCase())
    );

    // Add NLP suggestion for sentence-like commands, including punctuation from dictated text.
    if (input.trim().length > 3) {
      setSuggestions([
        {
          type: 'nlp',
          text: input,
          description: 'Execute scheduling command',
        },
        ...filtered,
      ]);
    } else {
      setSuggestions(filtered);
    }
  });

  // Filter suggestions based on input
  useEffect(() => {
    filterSuggestions(input);
  }, [input]);

  const handleSubmit = async (command?: CommandSuggestion) => {
    if (processingRef.current) return;
    const typedInput = input.trim();
    const selectedCommand = suggestions[selectedIndex];
    const cmd =
      command ||
      (typedInput && (!selectedCommand || selectedCommand.text !== typedInput)
        ? {
            type: 'nlp' as const,
            text: typedInput,
            description: 'Execute scheduling command',
          }
        : selectedCommand) ||
      (typedInput
        ? {
            type: 'nlp' as const,
            text: typedInput,
            description: 'Execute scheduling command',
          }
        : undefined);
    if (!cmd) return;

    processingRef.current = true;
    setIsProcessing(true);
    setStatusMessage(cmd.type === 'nlp' ? 'Interpreting your request...' : 'Running command...');

    try {
      if (cmd.type === 'nlp') {
        const stateHandled = await tryHandleStateCommand(cmd.text);
        if (stateHandled) {
          onClose();
          return;
        }

        // Send to NLP API
        const result = await api.nlp.command(cmd.text);
        setStatusMessage('Applying schedule changes...');

        if (result.success && result.data) {
          const notification = result.data.result?.notification;
          if (notification) {
            toast({
              title: notification.title,
              description: notification.message,
              variant:
                notification.type === 'error' || notification.type === 'infeasible'
                  ? 'destructive'
                  : 'default',
            });
          } else {
            toast({ title: 'Command processed successfully' });
          }

          applyOptimisticCommandResult(result.data);

          // If an activity was created or modified, refresh activities and blocks
          setStatusMessage('Refreshing activity board...');
          const snapshot = captureTaskSnapshot();
          const currentCalendar = captureCalendarReadFence();
          const [taskRes, blockRes] = await Promise.all([
            api.tasks.list(),
            api.schedule.getGhostBlocks(),
          ]);
          if (isTaskSnapshotCurrent(snapshot) && currentCalendar()) {
            if (taskRes.success && taskRes.data) {
              const tasksList = Array.isArray(taskRes.data)
                ? taskRes.data
                : taskRes.data?.tasks;
              if (tasksList) mergeRefreshedTasks(tasksList.map(taskToUI), setTasks);
            }
            if (blockRes.success && blockRes.data) {
              setBlocks(normalizeScheduleBlocks(blockRes.data));
            }
          }
          setStatusMessage('Done.');
        } else {
          const message = result.error?.message || 'Unknown error';
          setStatusMessage(message);
          toast({
            title: 'Failed to process command',
            description: message,
            variant: 'destructive',
          });
          return;
        }
        window.setTimeout(onClose, 450);
      } else {
        // Handle built-in commands
        if (cmd.action) {
          cmd.action();
        } else {
          onClose();
        }
      }
    } catch (error) {
      console.error('Command execution error:', error);
      const message = error instanceof Error ? error.message : 'Command failed.';
      setStatusMessage(message);
      toast({ title: 'Failed to process command', description: message, variant: 'destructive' });
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
    }
  };

  const tryHandleStateCommand = async (text: string): Promise<boolean> => {
    const water = parseWaterCommand(text);
    if (water) {
      setStatusMessage('Logging water...');
      const response = await api.state.logWater(water);
      if (!response.success) {
        toast({
          title: 'Water log failed',
          description: response.error?.message || 'Try the State page.',
          variant: 'destructive',
        });
        return true;
      }
      toast({ title: 'Water logged', description: `${water.amount} ${water.unit} added.` });
      window.dispatchEvent(new CustomEvent('flexiplanner-state-updated'));
      navigate('/state');
      return true;
    }

    const checkIn = parseCheckInCommand(text);
    if (checkIn) {
      setStatusMessage('Logging current state...');
      const response = await api.state.logCheckIn(checkIn);
      if (!response.success) {
        toast({
          title: 'State log failed',
          description: response.error?.message || 'Try the State page.',
          variant: 'destructive',
        });
        return true;
      }
      toast({ title: 'State logged', description: 'Current trajectory updated.' });
      window.dispatchEvent(new CustomEvent('flexiplanner-state-updated'));
      navigate('/state');
      return true;
    }

    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="gap-0 p-0 sm:max-w-[600px]">
        <DialogTitle className="sr-only">Command Palette</DialogTitle>
        <DialogDescription className="sr-only">
          Search for commands and perform actions
        </DialogDescription>
        {/* Input */}
        <div className="flex items-center gap-3 border-b p-4">
          <Command className="text-muted-foreground h-5 w-5" />
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Try "I need an hour now, free it up"'
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
            disabled={isProcessing}
          />
          {isProcessing && <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />}
        </div>

        {/* Suggestions */}
        <div className="max-h-[400px] overflow-y-auto">
          {suggestions.length === 0 ? (
            <div className="text-muted-foreground p-8 text-center">No commands found</div>
          ) : (
            <div className="p-2">
              {suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSubmit(suggestion)}
                  className={cn(
                    'w-full rounded-md p-3 text-left transition-colors',
                    idx === selectedIndex ? 'bg-accent' : 'hover:bg-accent/50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    {suggestion.type === 'nlp' && (
                      <Sparkles className="text-primary h-4 w-4 flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{suggestion.text}</div>
                      {suggestion.description && (
                        <div className="text-muted-foreground truncate text-sm">
                          {suggestion.description}
                        </div>
                      )}
                    </div>
                    {suggestion.type === 'nlp' && (
                      <Badge variant="secondary" className="flex-shrink-0">
                        AI
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-muted-foreground flex flex-col items-stretch gap-2 border-t p-2 text-xs sm:flex-row sm:items-center sm:justify-between">
          <div className="hidden items-center gap-4 pl-2 sm:flex">
            <span>↑↓ Navigate</span>
            <span>↵ Execute</span>
            <span>Esc Close</span>
          </div>
          {statusMessage && (
            <div className="text-muted-foreground min-w-0 flex-1 truncate px-2 py-2 text-left text-xs sm:text-right">
              {statusMessage}
            </div>
          )}
          <Button
            type="button"
            size="sm"
            onClick={() => handleSubmit()}
            disabled={isProcessing || !input.trim()}
            className="h-9 shrink-0"
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CornerDownLeft className="h-4 w-4" />
            )}
            Execute
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Small number-word vocabulary recognized by the local hydration-command parser. */
const numberWords: Record<string, number> = {
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};

/** Recognize a hydration phrase with an explicit supported amount/unit and return a normalized log draft plus the original note. Nonmatching text returns null for the general command path. */
function parseWaterCommand(text: string): null | {
  /** Recognized numeric amount in the normalized unit. */ amount: number;
  /** Normalized hydration unit: bottle, glass, oz, or ml. */ unit: WaterUnit;
  /** Original trimmed command text retained as the observation note. */ note: string;
} {
  const normalized = text.toLowerCase().replace(/\s+/g, ' ').trim();
  if (!/\b(water|drank|drink|hydrated|hydration)\b/.test(normalized)) return null;

  const match = normalized.match(
    /\b(\d+(?:\.\d+)?|a|an|one|two|three|four|five|six|seven|eight|nine|ten)\s*(glasses?|bottles?|oz|ounces?|ml|milliliters?)\b/
  );

  if (!match) return null;

  const amount = parseAmount(match[1]!);
  const unitText = match[2]!;
  const unit = unitText.startsWith('bottle')
    ? 'bottle'
    : unitText.startsWith('oz') || unitText.startsWith('ounce')
      ? 'oz'
      : unitText.startsWith('ml') || unitText.startsWith('milliliter')
        ? 'ml'
        : 'glass';

  return { amount, unit, note: text.trim() };
}

/** Recognize state-check wording and extract supported energy/resistance/hydration scores while preserving raw text. Unspecified scores remain null; this local parser does not submit the log. */
function parseCheckInCommand(text: string): null | Record<string, unknown> {
  const normalized = text.toLowerCase().replace(/\s+/g, ' ').trim();
  const looksLikeState =
    /\b(i feel|feeling|state check|check in|energy|emotion|body|resistance|rj|rejuvenation|productive)\b/.test(
      normalized
    );
  if (!looksLikeState) return null;

  const productivity = extractScore(
    normalized,
    /\b(?:p|productivity|productive)\s*(?:energy)?\s*(?:is|=|:)?\s*(\d{1,2})\b/
  );
  const rejuvenation = extractScore(
    normalized,
    /\b(?:rj|rejuvenation|relaxed)\s*(?:energy)?\s*(?:is|=|:)?\s*(\d{1,2})\b/
  );
  const resistance = extractScore(normalized, /\bresistance\s*(?:is|=|:)?\s*(\d{1,2})\b/);
  const hydration = extractScore(normalized, /\bhydration\s*(?:is|=|:)?\s*(\d{1,2})\b/);

  return {
    raw_text: text.trim(),
    productivity_energy: productivity,
    rejuvenation_energy: rejuvenation,
    resistance,
    hydration_feeling: hydration,
  };
}

/** Convert a matched numeric amount or supported number word; unknown words fall back to one within the hydration grammar. */
function parseAmount(value: string): number {
  if (/^\d/.test(value)) return Number(value);
  return numberWords[value] || 1;
}

/** Apply a successful command response to the local activity store before refresh. Existing records receive returned placement; new placeholders require an inferred title and select a bundle only when exactly one active choice exists. */
function applyOptimisticCommandResult(data: NlpCommandResult | undefined) {
  const result = data?.result;
  const intent = data?.intent;
  if (!result?.success || !result.task_id) return;

  const store = useTasksStore.getState();
  const existing = store.getTask(result.task_id);
  const slot = result.new_slot;

  if (existing) {
    store.updateTask(result.task_id, {
      scheduledStart: slot?.start ?? existing.scheduledStart,
      scheduledEnd: slot?.end ?? existing.scheduledEnd,
    });
    return;
  }

  const title = inferCreatedTitle(intent);
  if (!title) return;

  const now = new Date().toISOString();
  const duration = inferCreatedDuration(intent);
  const workspace = useWorkspaceStore.getState();
  const activeBundles = workspace.bundles.filter(bundle => bundle.status === 'active');
  const bundleId = activeBundles.length === 1 ? activeBundles[0]!.id : null;
  const projectId = workspace.getBundle(bundleId)?.projectId || null;
  const optimisticTask: TaskUI = {
    id: result.task_id,
    userId: '',
    parentId: null,
    bundleId,
    projectId,
    teamId: null,
    assignedToUserId: null,
    createdByUserId: null,
    title,
    status: 'todo',
    priority: 3,
    depth: 0,
    position: store.tasks.length,
    estimatedDuration: duration,
    remainingDuration: duration,
    completionPercent: 0,
    deadline: null,
    energyLevel: undefined,
    stateProfile: intent?.state_profile,
    scheduledStart: slot?.start ?? null,
    scheduledEnd: slot?.end ?? null,
    completedAt: null,
    isRecurring: false,
    recurrenceRule: null,
    customAttributes: {},
    createdAt: now,
    updatedAt: now,
  };

  store.addTask(optimisticTask);
}

/** Merge refreshed records by ID into the current store, retaining local records missing from this response. This merge is not a deletion reconciliation or a version-conflict resolver. */
function mergeRefreshedTasks(refreshedTasks: TaskUI[], setTasks: (tasks: TaskUI[]) => void) {
  const merged = new Map(useTasksStore.getState().tasks.map(task => [task.id, task]));
  for (const task of refreshedTasks) {
    merged.set(task.id, task);
  }
  setTasks(Array.from(merged.values()));
}

/** Extract a title from direct create intents or the first nested create_task operation. Unrelated or incomplete intents return null. */
function inferCreatedTitle(intent: ScheduleIntent | null | undefined): string | null {
  if (!intent) return null;
  if (intent.action === 'create_task' || intent.action === 'create_and_schedule') {
    return intent.task_identifier || null;
  }
  const createOperation = Array.isArray(intent.operations)
    ? intent.operations.find(operation => operation.type === 'create_task')
    : null;
  return createOperation?.title || createOperation?.task_identifier || null;
}

/** Read a finite duration from a direct intent or its first create_task operation; missing numeric evidence remains null. */
function inferCreatedDuration(intent: ScheduleIntent | null | undefined): number | null {
  if (!intent) return null;
  const direct = intent.duration_minutes;
  if (typeof direct === 'number' && Number.isFinite(direct)) return direct;
  const createOperation = Array.isArray(intent.operations)
    ? intent.operations.find(operation => operation.type === 'create_task')
    : null;
  const nested = createOperation?.duration_minutes;
  return typeof nested === 'number' && Number.isFinite(nested) ? nested : null;
}

/** Extract the first captured score and clamp it to 0–10; unmatched or nonfinite values remain null. */
function extractScore(text: string, pattern: RegExp): number | null {
  const match = text.match(pattern);
  if (!match?.[1]) return null;
  const score = Number(match[1]);
  if (!Number.isFinite(score)) return null;
  return Math.max(0, Math.min(10, score));
}

/** Combine tentative ghost placements with recorded work/pause segments for immediate command-result rendering. Pause records receive free-time presentation; conversion does not persist scheduling. */
function normalizeScheduleBlocks(data: Pick<GhostBlocksData, 'ghost_blocks' | 'work_segments'>) {
  const ghostBlocks = (data.ghost_blocks || []).map(block => ({
    id: block.id,
    taskId: block.task_id,
    title: block.title || 'Suggested activity',
    startTime: block.proposed_start,
    endTime: block.proposed_end,
    isGhost: true,
  }));

  const workSegments = (data.work_segments || []).map(segment => ({
    id: segment.id,
    taskId: segment.task_id ?? '',
    title: segment.segment_type === 'pause' ? 'Free time' : segment.title || 'Focused work',
    startTime: segment.start_time,
    endTime: segment.end_time,
    isGhost: false,
    priority: segment.segment_type === 'pause' ? 5 : 3,
    requiredEnergy: segment.segment_type === 'pause' ? 0 : undefined,
  }));

  return [...ghostBlocks, ...workSegments];
}
